import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Config } from '../../config';

/**
 * Platform state, compressed into a rail card.
 *
 * The same exception checks that briefly had the whole landing page. They are
 * worth surfacing — a scan whose worker died says "running" forever, and
 * nothing else will tell you — but they are housekeeping, not the reason to
 * open the platform, and giving them the page said otherwise.
 *
 * So: counts, the three worst by urgency, and a way through to each. Silent
 * when there is nothing, rather than occupying space to report zero.
 */

const TONE = {
  blocking: 'text-red-400',
  attention: 'text-amber-400',
  watch: 'text-slate-400',
};

const DOT = {
  blocking: '#dc2626',
  attention: '#f59e0b',
  watch: '#64748b',
};

function OpenItems({ onOpen }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${Config.APIBaseURL}/attention`, {
      headers: { access_token: `Bearer ${token}` },
      withCredentials: true,
    })
      .then(({ data: result }) => setData(result))
      // Non-admins get a 403 and simply do not see the card. A rail widget is
      // not the place to explain an authorisation model.
      .catch(() => setData(null));
  }, []);

  const items = data?.items || [];
  if (!data || items.length === 0) return null;

  const counts = data.counts || {};
  const shown = expanded ? items : items.slice(0, 3);

  return (
    <div className="rounded-lg bg-slate-900/50 border border-slate-800 p-4">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm font-semibold text-slate-200">Open items</h2>
        <span className="text-[11px] text-slate-500">
          {counts.blocking > 0 && (
            <span className="text-red-400">{counts.blocking} blocking · </span>
          )}
          {items.length} total
        </span>
      </div>

      <div className="space-y-2">
        {shown.map((item) => (
          <button
            key={item.key}
            onClick={() => onOpen(item.link)}
            className="w-full text-left flex items-start gap-2 py-1 group"
          >
            <span
              className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0"
              style={{ backgroundColor: DOT[item.urgency] }}
            />
            <span className="text-xs text-slate-400 group-hover:text-slate-200 flex-1 min-w-0">
              {item.title}
            </span>
            <span className={`text-xs tabular-nums shrink-0 ${TONE[item.urgency]}`}>
              {item.count}
            </span>
          </button>
        ))}
      </div>

      {items.length > 3 && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 text-[11px] text-slate-500 hover:text-slate-300"
        >
          {expanded ? 'Show less' : `${items.length - 3} more`}
        </button>
      )}

      {data.checks_failed > 0 && (
        <p className="mt-2 text-[10px] text-amber-500/70">
          {data.checks_failed} check{data.checks_failed === 1 ? '' : 's'} could not run — this list is incomplete.
        </p>
      )}
    </div>
  );
}

export default OpenItems;
